import { createSlice } from '@reduxjs/toolkit'
import { lmDevicesUsersInitialState as initialState } from '../initialState/lmDevicesUsersState'
import { apiCallBegan } from '../api'

function extractError(payload) {
  if (payload && payload.errors && payload.errors.length > 0) {
    return payload.errors[0].desc || payload.errors[0].message || 'Unknown error'
  }
  if (payload && payload.message) {
    return payload.message
  }
  if (typeof payload === 'string') {
    return payload
  }
  return 'Unknown error'
}

const slice = createSlice({
  name: 'lmDevicesUsers',
  initialState,
  reducers: {
    // --- Device Stats ---
    deviceStatsRequested: (state) => {
      state.deviceStatsLoading = true
      state.deviceStatsError = null
    },
    deviceStatsReceived: (state, action) => {
      state.deviceStats = action.payload.response.data[0]
      state.deviceStatsLoading = false
    },
    deviceStatsFailed: (state, action) => {
      state.deviceStatsLoading = false
      state.deviceStatsError = extractError(action.payload)
    },

    // --- Device List ---
    devicesRequested: (state) => {
      state.devicesLoading = true
      state.devicesError = null
    },
    devicesReceived: (state, action) => {
      const { data: devices, filters } = action.payload.response
      state.devices = devices
                        .map(device => ({
                          ...device,
                          deviceName: device.OSComputerName || device.DeviceIdentifier || device.deviceId,
                        }))
      if (filters) {
        state.devicesFilters = filters
        state.isDevicesLastPage = devices.length < filters.pageSize ? true : false
        }
      else {
        state.isDevicesLastPage = devices.length < state.devicesFilters.pageSize ? true : false
      }
      let currentDevice = devices[0] || null
      state.selectedDevice = { ...currentDevice, deviceName: currentDevice?.OSComputerName || currentDevice?.DeviceIdentifier || currentDevice?.deviceId }
      state.devicesLoading = false
    },
    devicesFailed: (state, action) => {
      state.devicesLoading = false
      state.devicesError = extractError(action.payload)
      state.devices = []
    },
    setDevicesFilters: (state, action) => {
      state.devicesFilters = action.payload
    },
    resetDevices: (state) => {
      state.devices = initialState.devices
      state.devicesLoading = initialState.devicesLoading
      state.devicesError = initialState.devicesError
      state.devicesTotalRecords = initialState.devicesTotalRecords
      state.devicesFilters = { ...initialState.devicesFilters }
    },

    selectDevice: (state, action) => {
      state.selectedDevice = action.payload.response.data
    },

    // --- User Stats ---
    userStatsRequested: (state) => {
      state.userStatsLoading = true
      state.userStatsError = null
    },
    userStatsReceived: (state, action) => {
      state.userStats = action.payload.response.data
      state.userStatsLoading = false
    },
    userStatsFailed: (state, action) => {
      state.userStatsLoading = false
      state.userStatsError = extractError(action.payload)
    },

    // --- User List ---
    usersRequested: (state) => {
      state.usersLoading = true
      state.usersError = null
    },
    usersReceived: (state, action) => {
      const { data: users, filters } = action.payload.response
      state.users = users.map(user => ({
                        ...user,
                        userName: user.UserIdentifier || user.userId,
                      }))
      if (filters) {
        state.usersFilters = filters
        state.isUsersLastPage = users.length < filters.pageSize ? true : false
      }
      else{
        state.isUsersLastPage = users.length < state.usersFilters.pageSize ? true : false
      }
      state.selectedUser = users[0] || null
      state.usersLoading = false
    },
    usersFailed: (state, action) => {
      state.usersLoading = false
      state.usersError = extractError(action.payload)
      state.users = []
    },
    setUsersFilters: (state, action) => {
      state.usersFilters = action.payload
    },
    resetUsers: (state) => {
      state.users = initialState.users
      state.usersLoading = initialState.usersLoading
      state.usersError = initialState.usersError
      state.usersTotalRecords = initialState.usersTotalRecords
      state.usersFilters = { ...initialState.usersFilters }
    },
    selectUser: (state, action) => {
      state.selectedUser = action.payload.response.data
    },
    // --- Users for a Device ---
    deviceUsersRequested: (state) => {
      state.deviceUsersLoading = true
      state.deviceUsersError = null
    },
    deviceUsersReceived: (state, action) => {
      state.deviceUsers = action.payload.response.data.map(user => ({
        ...user,
        showInstalls: user.showInstalls || false,
      }))
      state.deviceUsersLoading = false
    },
    deviceUsersFailed: (state, action) => {
      state.deviceUsersLoading = false
      state.deviceUsersError = extractError(action.payload)
      state.deviceUsers = []
    },

    // --- Devices for a User ---
    userDevicesRequested: (state) => {
      state.userDevicesLoading = true
      state.userDevicesError = null
    },
    userDevicesReceived: (state, action) => {
      state.userDevices = action.payload.response.data
      state.userDevicesLoading = false
    },
    userDevicesFailed: (state, action) => {
      state.userDevicesLoading = false
      state.userDevicesError = extractError(action.payload)
      state.userDevices = []
    },

    // --- Extension Installs ---
    extensionInstallsRequested: (state) => {
      state.extensionInstallsLoading = true
      state.extensionInstallsError = null
    },
    extensionInstallsReceived: (state, action) => {
      state.extensionInstalls = [...state.extensionInstalls, ...action.payload.response.data]
      state.extensionInstallsLoading = false
    },
    extensionInstallsFailed: (state, action) => {
      state.extensionInstallsLoading = false
      state.extensionInstallsError = extractError(action.payload)
      state.extensionInstalls = []
    },

    // --- General ---
    resetLmDevicesUsers: () => initialState,
    resetLmMessages: (state) => {
      state.userMsg = null
      state.statusCode = null
    },
  },
})

export const {
  deviceStatsRequested,
  deviceStatsReceived,
  deviceStatsFailed,
  devicesRequested,
  devicesReceived,
  devicesFailed,
  setDevicesFilters,
  resetDevices,
  userStatsRequested,
  userStatsReceived,
  userStatsFailed,
  usersRequested,
  usersReceived,
  usersFailed,
  setUsersFilters,
  resetUsers,
  deviceUsersRequested,
  deviceUsersReceived,
  deviceUsersFailed,
  userDevicesRequested,
  userDevicesReceived,
  userDevicesFailed,
  extensionInstallsRequested,
  extensionInstallsReceived,
  extensionInstallsFailed,
  resetLmDevicesUsers,
  resetLmMessages,
  selectDevice,
  selectUser,
} = slice.actions
export default slice.reducer

// --- Action Creators ---

export const getDeviceStats = (selectedId, defaultCustomerId, isGlobalView) => {
  return (dispatch) => {
    const url = `portal/${isGlobalView ? 'tenants' : 'customers'}/${selectedId}/stats/device`
    return dispatch(apiCallBegan({
      urlPath: url,
      method: 'GET',
      filters: isGlobalView ? { customerId: defaultCustomerId } : {},
      onStart: deviceStatsRequested.type,
      onSuccess: deviceStatsReceived.type,
      onError: deviceStatsFailed.type,
    }))
  }
}

export const getDevices = (filters, selectedId, defaultCustomerId, isGlobalView, deviceId = null) => {
  return (dispatch) => {
    const url = `portal/${isGlobalView ? 'tenants' : 'customers'}/${selectedId}/devices${deviceId ? `/${deviceId}` : ''}`
    return dispatch(apiCallBegan({
      urlPath: url,
      method: 'GET',
      filters: isGlobalView ? { ...filters, customerId: defaultCustomerId } : filters,
      onStart: devicesRequested.type,
      onSuccess: devicesReceived.type,
      onError: devicesFailed.type,
    }))
  }
}

export const getDeviceById = (selectedId, deviceId, defaultCustomerId, isGlobalView) => {
  return (dispatch) => {
    const url = `portal/${isGlobalView ? 'tenants' : 'customers'}/${selectedId}/devices/${deviceId}`
    return dispatch(apiCallBegan({
      urlPath: url,
      method: 'GET',
      filters: isGlobalView ? { customerId: defaultCustomerId } : {},
      onStart: devicesRequested.type,
      onSuccess: devicesReceived.type,
      onError: devicesFailed.type,
    }))
  }
}

export const getUserStats = (selectedId, defaultCustomerId, isGlobalView) => {
  return (dispatch) => {
    const url = `portal/${isGlobalView ? 'tenants' : 'customers'}/${selectedId}/stats/user`
    return dispatch(apiCallBegan({
      urlPath: url,
      method: 'GET',
      filters: isGlobalView ? { customerId: defaultCustomerId } : {},
      onStart: userStatsRequested.type,
      onSuccess: userStatsReceived.type,
      onError: userStatsFailed.type,
    }))
  }
}

export const getLmUsers = (filters, selectedId, defaultCustomerId, isGlobalView, userId = null) => {
  return (dispatch) => {
    const url = `portal/${isGlobalView ? 'tenants' : 'customers'}/${selectedId}/users${userId ? `/${userId}` : ''}`
    return dispatch(apiCallBegan({
      urlPath: url,
      method: 'GET',
      filters: isGlobalView ? { ...filters, customerId: defaultCustomerId } : filters,
      onStart: usersRequested.type,
      onSuccess: usersReceived.type,
      onError: usersFailed.type,
    }))
  }
}

export const getLmUserById = (selectedId, userId, defaultCustomerId, isGlobalView) => {
  return (dispatch) => {
    const url = `portal/${isGlobalView ? 'tenants' : 'customers'}/${selectedId}/user/${userId}`
    return dispatch(apiCallBegan({
      urlPath: url,
      method: 'GET',
      filters: isGlobalView ? { customerId: defaultCustomerId } : {},
      onStart: usersRequested.type,
      onSuccess: usersReceived.type,
      onError: usersFailed.type,
    }))
  }
}

export const getUsersForDevice = (deviceId, customerId) => {
  return (dispatch) => {
    const url = `portal/customers/${customerId}/device/${deviceId}/user`
    return dispatch(apiCallBegan({
      urlPath: url,
      method: 'GET',
      onStart: deviceUsersRequested.type,
      onSuccess: deviceUsersReceived.type,
      onError: deviceUsersFailed.type,
    }))
  }
}

export const getDevicesForUser = (userId, customerId) => {
  return (dispatch) => {
    const url = `portal/customers/${customerId}/user/${userId}/device`
    return dispatch(apiCallBegan({
      urlPath: url,
      method: 'GET',
      onStart: userDevicesRequested.type,
      onSuccess: userDevicesReceived.type,
      onError: userDevicesFailed.type,
    }))
  }
}

export const getExtensionInstalls = (selectedId, userId, deviceId) => {
  return (dispatch) => {
    const url = `portal/customers/${selectedId}/extension-installs`
    const filters = { userId, deviceId }
    return dispatch(apiCallBegan({
      urlPath: url,
      method: 'GET',
      filters,
      onStart: extensionInstallsRequested.type,
      onSuccess: extensionInstallsReceived.type,
      onError: extensionInstallsFailed.type,
    }))
  }
}

export const setCurrentDevice = (device) => {
  return (dispatch) => {
    dispatch(selectDevice({ response: { data: device } }))
  }
}

export const selectUsr = (user) => {
  return (dispatch) => {
    dispatch(selectUser({ response: { data: user } }))
  }
}

export const setShowInstallsForUser = (userId, showInstalls) => {
  return (dispatch, getState) => {
    const { deviceUsers } = getState().entities.lmDevicesUsers
    const updatedUsers = deviceUsers.map(user => {
      if (String(user.userId) === String(userId)) {
        return { ...user, showInstalls: showInstalls }
      }
      return user;
    })
    dispatch(deviceUsersReceived({ response: { data: updatedUsers } }))
  }
}